import { Product } from "@/sdk/types";
import { cloneNode } from "@/ui-sdk/ui-script/utils";

export const getFriendlyName = (filterKey: string): string => {
  const segments = filterKey.split(".");
  const lastSegment = segments.pop(); // Get the very last part of the key

  if (lastSegment === "isMulticolor") {
    return "Multicolor";
  }
  if (lastSegment?.includes("value")) {
    return "Color";
  }

  const nameParts = segments.slice(2);
  if (lastSegment) {
    nameParts.push(lastSegment);
  }

  return nameParts
    .map((word) => word.replace(/_/g, " ")) // Replace underscores with spaces
    .map((word) => {
      return word
        .split(" ")
        .map((subWord) => subWord.charAt(0).toUpperCase() + subWord.slice(1))
        .join(" ");
    })
    .join(" ");
};
export function shouldHideFilter(key: string): boolean {
  const hiddenFilters = ["secondary", "tertiary", "primary_material", "shade"];

  return hiddenFilters.some((filter) => key.includes(filter));
}

export function getDisplayValue(
  value: string | number,
): string | number | boolean {
  if (value === 0) return false;
  if (value === 1) return true;
  return typeof value === "string" && value.length > 25
    ? `${value.substring(0, 22)}...`
    : value;
}

export const transformFilterData = (
  filterData: Record<string, string[] | string>,
): Record<string, (string | boolean)[]> => {
  const transformedData: Record<string, (string | boolean)[]> = {};

  Object.entries(filterData).forEach(([key, value]) => {
    const newKey = key.includes("tag") ? key.replace("tag", "tags") : key;

    const toTypedValue = (v: string): string | boolean =>
      v === "0" ? false : v === "1" ? true : v;

    if (Array.isArray(value)) {
      transformedData[newKey] = value.map(toTypedValue);
    } else {
      transformedData[newKey] = [toTypedValue(value)];
    }
  });

  return transformedData;
};

export const renderCloneNode = async (item: Product) => {
  const templateNode = window.helium.datastore.memory.html.product;

  if (!templateNode) return null;

  const existingProduct = (
    await window.helium.datastore.getProduct({
      id: window?.helium?.productCardHandle ?? "",
    })
  ).details;

  const node = cloneNode(templateNode, item, existingProduct);

  node.className += " ";
  return { __html: node.outerHTML };
};

export function showUnmatchedFiltersMessage(
  unmatchedFilters,
  productAreaWrapper,
  filters,
) {
  // Create a message string based on the unmatched filters
  let messageContent = "Could not find product for ";
  const correctedFilters = unmatchedFilters.map((filter) =>
    filter.replace("tags", "tag"),
  );

  const filterMessages = correctedFilters
    .map((filter) => {
      // Check if the filter key exists in the filters object
      if (filters.hasOwnProperty(filter)) {
        // Split the filter key, capitalize the first letter of each part, and create the formatted string
        const key = filter.split(".").pop().replace("_", " ");
        const value = filters[filter];

        // Capitalize the first letter of the key and value
        const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
        const formattedValue = value.charAt(0).toUpperCase() + value.slice(1);

        return `${formattedKey}: ${formattedValue}`;
      }
      return null; // For unmatched filter keys, we can ignore
    })
    .filter(Boolean);
  // If there are any matching filter messages, create the message string
  if (filterMessages.length > 0) {
    messageContent += filterMessages.join(" and ");
  }

  const existingMessage =
    productAreaWrapper?.parentNode?.querySelector(".filter-message");

  if (existingMessage) {
    // If the message exists, update its content
    existingMessage.textContent = messageContent;
  } else {
    // If the message does not exist, create a new message element
    const message = document.createElement("div");
    message.classList.add("filter-message"); // Add a class for easier identification
    message.style.textAlign = "center";
    message.style.color = "#212326";
    message.style.fontSize = "12px";
    message.textContent = messageContent;
    message.style.display = "block";

    // Insert the message before the product area wrapper
    productAreaWrapper?.parentNode?.insertBefore(message, productAreaWrapper);
  }

  productAreaWrapper?.parentNode?.insertBefore(
    messageContent,
    productAreaWrapper,
  );
}
